import {
    Box,
    CircularProgress,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Person as PersonIcon
} from '@mui/icons-material';
import {
    useMemo,
    useState
} from 'react';

import { DeleteMemberConfirmationDialog } from './DeleteMemberConfirmationDialog';
import { MemberChip } from './MemberChip';
import { OwnerChip } from './OwnerChip';
import { RoleChangeConfirmationDialog } from './RoleChangeConfirmationDialog';
import { TeamMember } from '../../API';
import { TeamType } from '../../models';
import { useTeamManagement } from './useTeamManagement';
import { useUser } from '../../components/user/UserContext';

type RoleChangeProps = {
    open: boolean;
    member: TeamMember | null;
    newRole: TeamType | null;
    isSelfDemotion?: boolean;
}

type DeleteProps = {
    open: boolean;
    member: TeamMember | null;
}

export const TeamMembersTab = () => {
    const { user } = useUser();
    const currentUserId = user?.getUsername();
    const { getUserById, ownerTeamMembers, memberTeamMembers, handleRoleChange, handleRemoveMember } = useTeamManagement();

    const [loadingRemoveId, setLoadingRemoveId] = useState<string | null>(null);
    const [loadingRoleChangeId, setLoadingRoleChangeId] = useState<string | null>(null);
    const [roleChangeConfirmation, setRoleChangeConfirmation] = useState<RoleChangeProps>({
        open: false,
        member: null,
        newRole: null,
        isSelfDemotion: false
    });
    const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteProps>({
        open: false,
        member: null
    });

    const onRemoveMember = async (member: TeamMember) => {
        setDeleteConfirmation({
            open: true,
            member
        });
    };

    const onRoleChange = async (member: TeamMember, newRole: TeamType) => {
        const isSelfDemotion = member.userId === currentUserId && ownerTeamMembers?.some(owner => owner.userId === currentUserId);
        if (newRole === TeamType.OWNER || isSelfDemotion) {
            setRoleChangeConfirmation({
                open: true,
                member,
                newRole,
                isSelfDemotion
            });
            return;
        }

        try {
            setLoadingRoleChangeId(member.userId);
            await handleRoleChange(member, newRole);
        } finally {
            setLoadingRoleChangeId(null);
        }
    };

    const handleRoleChangeConfirm = async () => {
        if (!roleChangeConfirmation.member || !roleChangeConfirmation.newRole) return;

        // Close dialog immediately
        setRoleChangeConfirmation({ open: false, member: null, newRole: null, isSelfDemotion: false });

        try {
            setLoadingRoleChangeId(roleChangeConfirmation.member.userId);
            await handleRoleChange(roleChangeConfirmation.member, roleChangeConfirmation.newRole);
        } finally {
            setLoadingRoleChangeId(null);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirmation.member) return;

        try {
            setLoadingRemoveId(deleteConfirmation.member.userId);
            setDeleteConfirmation({ open: false, member: null });
            await handleRemoveMember(deleteConfirmation.member);
        } finally {
            setLoadingRemoveId(null);
        }
    };

    const allMembers = useMemo(() => {
        return [
            ...(ownerTeamMembers?.map(member => ({ ...member, isOwner: true })) ?? []),
            ...(memberTeamMembers?.map(member => ({ ...member, isOwner: false })) ?? [])
        ].sort((a, b) => {
            // Move the current user to the top 
            if (a.userId === currentUserId) return -1;
            if (b.userId === currentUserId) return 1;
            return 0;
        });
    }, [ownerTeamMembers, memberTeamMembers, currentUserId]);

    return (
        <>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Joined Date</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {allMembers.map((member) => (
                            <TableRow key={member.isOwner ? member.userId : member.id}>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <PersonIcon sx={{ mr: 1 }} />
                                        {`${getUserById(member.userId)?.name}${member.userId === currentUserId ? ' (You)' : ''}`}
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    {loadingRoleChangeId === member.userId ? (
                                        <CircularProgress size={20} />
                                    ) : member.isOwner && ownerTeamMembers?.length === 1 ? (
                                        <OwnerChip />
                                    ) : (
                                        <Select
                                            value={member.isOwner ? TeamType.OWNER : TeamType.MEMBER}
                                            onChange={(e) => onRoleChange(member, e.target.value as TeamType)}
                                            size="small"
                                            sx={{
                                                minWidth: 100,
                                                border: 'none',
                                                '& .MuiSelect-select': {
                                                    py: 0.5,
                                                    px: 0
                                                },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    border: 'none'
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    border: 'none'
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    border: 'none'
                                                }
                                            }}
                                        >
                                            <MenuItem value={TeamType.OWNER}>
                                                <OwnerChip />
                                            </MenuItem>
                                            <MenuItem value={TeamType.MEMBER}>
                                                <MemberChip />
                                            </MenuItem>
                                        </Select>
                                    )}
                                </TableCell>
                                <TableCell>{member.joinedAt ? new Date(member.joinedAt).toLocaleDateString() : ''}</TableCell>
                                <TableCell align="right">
                                    {member.userId !== currentUserId && (
                                        <IconButton
                                            onClick={() => onRemoveMember(member)}
                                            color="error"
                                            disabled={loadingRemoveId === member.userId || loadingRoleChangeId === member.userId}
                                            size="small"
                                        >
                                            {loadingRemoveId === member.userId ? <CircularProgress size={20} /> : <DeleteIcon />}
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <DeleteMemberConfirmationDialog
                open={deleteConfirmation.open}
                member={deleteConfirmation.member}
                onClose={() => setDeleteConfirmation({ open: false, member: null })}
                onConfirm={handleDeleteConfirm}
            />
            <RoleChangeConfirmationDialog
                open={roleChangeConfirmation.open}
                member={roleChangeConfirmation.member}
                isSelfDemotion={roleChangeConfirmation.isSelfDemotion}
                onClose={() => setRoleChangeConfirmation({ open: false, member: null, newRole: null, isSelfDemotion: false })}
                onConfirm={handleRoleChangeConfirm}
            />
        </>
    );
}; 